import { reverse } from 'named-urls'
import React from 'react'

import type { TimelineEvent } from '@fourthwall/components'
import { ButtonClean, PriceString } from '@fourthwall/components'
import { routing } from '@utils/routing'

import { useCurrentShop } from '../../../../../hooks/useCurrentShop'
import { useGiftCardGroupTimelineQuery } from '../queries/useGiftCardGroupTimelineQuery'
import { useGiftCardTimelineQuery } from '../queries/useGiftCardTimelineQuery'

type UseGiftCardTimelineParams = {
  giftCardId: string
  isCreating: boolean
  isGiftCardGroup: boolean
}

type GiftCardEvent =
  | NonNullable<
      ReturnType<typeof useGiftCardTimelineQuery>['giftCardTimelineQuery']['data']
    >['events'][number]
  | NonNullable<
      ReturnType<typeof useGiftCardGroupTimelineQuery>['giftCardGroupTimelineQuery']['data']
    >['events'][number]

const getEventDescription = (event: GiftCardEvent, giftCardBalanceBaseUri: string) => {
  if (event.type === 'CREATION') {
    return 'Gift card created'
  }

  if (event.type === 'RECIPIENT_ADDED') {
    return (
      <>
        Gift card (
        <ButtonClean
          label={event.giftCardDetails.code}
          href={`${giftCardBalanceBaseUri}${event.giftCardDetails.giftCardId}`}
          target="_blank"
        />
        ) has been sent to {event.email}
      </>
    )
  }

  if (event.type === 'ORDER') {
    return (
      <>
        <PriceString value={event.amountUsed.value} minimumFractionDigits={2} /> value from gift
        card{' '}
        <ButtonClean
          label={event.giftCardDetails.code}
          href={`${giftCardBalanceBaseUri}${event.giftCardDetails.giftCardId}`}
          target="_blank"
        />{' '}
        used in order{' '}
        <ButtonClean
          label={`#${event.orderFriendlyId}`}
          to={reverse(routing.contributions.orders.order.self, { orderId: event.orderId || '' })}
        />
        .
        <br />
        <PriceString value={event.balance.value} minimumFractionDigits={2} /> left to use for this
        gift card.
      </>
    )
  }
}

const useGiftCardTimeline = ({
  giftCardId,
  isCreating,
  isGiftCardGroup,
}: UseGiftCardTimelineParams) => {
  const { currentShop } = useCurrentShop()
  const { giftCardTimelineQuery } = useGiftCardTimelineQuery([{ id: giftCardId }], {
    enabled: !isCreating && !isGiftCardGroup,
  })
  const { giftCardGroupTimelineQuery } = useGiftCardGroupTimelineQuery([{ groupId: giftCardId }], {
    enabled: !isCreating && isGiftCardGroup,
  })
  const giftCardBalanceBaseUri = `${currentShop?.baseUri}/gift_cards/`

  const getGiftCardTimelineEvents = (): TimelineEvent[] => {
    if (isCreating) {
      return []
    }

    const source = isGiftCardGroup ? giftCardGroupTimelineQuery.data : giftCardTimelineQuery.data

    if (!source) {
      return []
    }

    const filteredEvents = source.events.filter((event, index, events) => {
      if (isGiftCardGroup && event.type === 'CREATION') {
        return events.findIndex((e) => e.type === 'CREATION') === index
      }

      return true
    })

    return filteredEvents.map((event) => ({
      id: event.createdAt,
      happenedAt: event.createdAt,
      description: getEventDescription(event, giftCardBalanceBaseUri),
    }))
  }

  return {
    isFetching: giftCardTimelineQuery.isFetching || giftCardGroupTimelineQuery.isFetching,
    timelineEvents: getGiftCardTimelineEvents(),
  }
}
export default useGiftCardTimeline
